import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import Icon from '../../../components/AppIcon';

const PatientSatisfactionMetrics = ({ dateRange, provider }) => {
  const satisfactionData = [
    { name: 'Very Satisfied', value: 245, percentage: 68 },
    { name: 'Satisfied', value: 85, percentage: 24 },
    { name: 'Neutral', value: 20, percentage: 6 },
    { name: 'Dissatisfied', value: 7, percentage: 2 }
  ];

  const COLORS = ['#059669', '#2563EB', '#D97706', '#DC2626'];

  const providerRatings = [
    { name: 'Dr. Michael Chen', rating: 4.9, reviews: 156, nps: 85 },
    { name: 'Dr. Sarah Rodriguez', rating: 4.8, reviews: 142, nps: 82 },
    { name: 'Dr. James Thompson', rating: 4.7, reviews: 128, nps: 78 }
  ];

  const keyMetrics = [
    {
      label: 'Overall NPS',
      value: '82',
      subtitle: 'Net Promoter Score',
      icon: 'TrendingUp',
      color: 'bg-success/10 text-success'
    },
    {
      label: 'Avg Rating',
      value: '4.8',
      subtitle: 'Out of 5.0',
      icon: 'Star',
      color: 'bg-warning/10 text-warning'
    },
    {
      label: 'Response Rate',
      value: '76%',
      subtitle: 'Survey completion',
      icon: 'MessageSquare',
      color: 'bg-primary/10 text-primary'
    },
    {
      label: 'Total Surveys',
      value: '357',
      subtitle: 'This period',
      icon: 'FileText',
      color: 'bg-muted text-muted-foreground'
    }
  ];

  return (
    <div className="bg-card rounded-xl border border-border shadow-elevation-2 p-4 md:p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg md:text-xl font-semibold text-foreground flex items-center gap-2">
          <Icon name="Heart" size={20} className="text-primary" />
          Patient Satisfaction Scores
        </h2>
        <span className="text-xs text-muted-foreground">
          {dateRange === 'day' ? 'Today' : dateRange === 'week' ? 'This Week' : dateRange === 'month' ? 'This Month' : dateRange === 'quarter' ? 'This Quarter' : 'This Year'}
        </span>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {keyMetrics?.map((metric, index) => (
          <div key={index} className="bg-muted/50 rounded-lg p-3 border border-border">
            <div className={`flex items-center justify-center w-8 h-8 rounded-lg ${metric?.color} mb-2`}>
              <Icon name={metric?.icon} size={16} />
            </div>
            <p className="text-2xl font-bold text-foreground mb-1">{metric?.value}</p>
            <p className="text-xs text-muted-foreground">{metric?.label}</p>
            <p className="text-xs text-muted-foreground">{metric?.subtitle}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <h3 className="text-sm font-semibold text-foreground mb-4">Satisfaction Distribution</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={satisfactionData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percentage }) => `${name}: ${percentage}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {satisfactionData?.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS?.[index % COLORS?.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'var(--color-card)',
                    border: '1px solid var(--color-border)',
                    borderRadius: '8px',
                    color: 'var(--color-foreground)'
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div>
          <h3 className="text-sm font-semibold text-foreground mb-4">Provider-Specific Ratings</h3>
          <div className="space-y-4">
            {providerRatings?.map((provider, index) => (
              <div key={index} className="bg-muted/50 rounded-lg p-4 border border-border">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-foreground">{provider?.name}</span>
                  <div className="flex items-center gap-1">
                    <Icon name="Star" size={14} className="text-warning fill-warning" />
                    <span className="text-sm font-bold text-foreground">{provider?.rating}</span>
                  </div>
                </div>
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>{provider?.reviews} reviews</span>
                  <span className="font-semibold text-success">NPS: {provider?.nps}</span>
                </div>
                <div className="mt-2 w-full bg-muted rounded-full h-2 overflow-hidden">
                  <div
                    className="h-full bg-success transition-all duration-500"
                    style={{ width: `${(provider?.rating / 5) * 100}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-6 pt-6 border-t border-border">
        <h3 className="text-sm font-semibold text-foreground mb-3">Recent Feedback Highlights</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="bg-success/10 rounded-lg p-3 border border-success/20">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="ThumbsUp" size={16} className="text-success" />
              <span className="text-xs font-semibold text-success">Most Praised</span>
            </div>
            <p className="text-sm text-foreground">Professional staff and clean facilities</p>
          </div>
          <div className="bg-primary/10 rounded-lg p-3 border border-primary/20">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="Clock" size={16} className="text-primary" />
              <span className="text-xs font-semibold text-primary">Wait Time</span>
            </div>
            <p className="text-sm text-foreground">Average 12 minutes - Excellent</p>
          </div>
          <div className="bg-warning/10 rounded-lg p-3 border border-warning/20">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="AlertTriangle" size={16} className="text-warning" />
              <span className="text-xs font-semibold text-warning">Improvement Area</span>
            </div>
            <p className="text-sm text-foreground">Appointment reminder frequency</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PatientSatisfactionMetrics;